import { FC, PropsWithChildren, ReactNode } from 'react';
import {
  DrfqPlatformStateStoreProvider,
  DrfqPlatformStateViewProvider,
  GrfqPlatformStateStoreProvider,
  GrfqPlatformStateViewProvider,
  FSPlatformStateStoreProvider,
  FSPlatformStateViewProvider,
  useDrfqPlatformState,
  useDrfqPlatformStateActions,
  useGrfqPlatformState,
  useGrfqPlatformStateActions,
  useFsPlatformState,
  useFsPlatformStateActions,
  PlatformStateState,
  PlatformStateActions,
  useFsPlatformStateView,
} from '#/platform-state/contexts/store';
import { PlatformStateWebsocketProvider } from '#/platform-state/contexts/websocket';
import { PlatformStateSubscriptionProvider } from '#/platform-state/contexts/subscriber';
import PlatformStateFetcherProvider from '#/platform-state/contexts/fetcher';
import { getPlatformState } from '@paradigm/api/src/future-spreads';
import { AsyncResp } from '@paradigm/api/src/fetch-api';
import { PlatformState, parsedPlatformStateValues } from '@paradigm/api/types';
import {
  getDrfqPlatformState,
  getGrfqPlatformState,
} from '#/unified-rfqs/repositories/platform-state';
import { getGrfqBaseUrl } from '#/unified-rfqs/repositories/grfq-api';
import { MergedPlatformStateProvider, useUMPlatformStateView } from './merger';

export { useFsPlatformStateView } from './store';
export { useUMPlatformStateView } from './merger';
interface Props {
  children: ReactNode;
}

interface PlatformStateProvidersProps extends Props {
  wsAddress: string;
  useNanoSeconds?: boolean;
  StoreProvider: (props: { children: ReactNode }) => JSX.Element;
  PlatformStateViewProvider: FC<
    PropsWithChildren<{
      usePlatformState: () => PlatformStateState;
    }>
  >;
  fetchSnapshot: () => AsyncResp<PlatformState>;
  usePlatformState: () => PlatformStateState;
  usePlatformStateActions: () => PlatformStateActions;
}

export function FsPlatformStateProviders({ children }: Props) {
  return (
    <PlatformStateProviders
      useNanoSeconds
      wsAddress={`${FS_URL_WS}/v1/fs`}
      StoreProvider={FSPlatformStateStoreProvider}
      PlatformStateViewProvider={FSPlatformStateViewProvider}
      fetchSnapshot={getPlatformState}
      usePlatformState={useFsPlatformState}
      usePlatformStateActions={useFsPlatformStateActions}
    >
      {children}
    </PlatformStateProviders>
  );
}

export function UMPlatformStateProviders({ children }: Props) {
  return (
    <DrfqPlatformStateProviders>
      <GrfqPlatformStateProviders>
        <MergedPlatformStateProvider>{children}</MergedPlatformStateProvider>
      </GrfqPlatformStateProviders>
    </DrfqPlatformStateProviders>
  );
}

function GrfqPlatformStateProviders({ children }: Props) {
  const grfqWsAddress = `${PAPI_URL_WS}${getGrfqBaseUrl()}`;

  return (
    <PlatformStateProviders
      wsAddress={grfqWsAddress}
      StoreProvider={GrfqPlatformStateStoreProvider}
      PlatformStateViewProvider={GrfqPlatformStateViewProvider}
      fetchSnapshot={getGrfqPlatformState}
      usePlatformState={useGrfqPlatformState}
      usePlatformStateActions={useGrfqPlatformStateActions}
    >
      {children}
    </PlatformStateProviders>
  );
}

function DrfqPlatformStateProviders({ children }: Props) {
  const drfqWsAddress = `${PAPI_URL_WS}/v2/drfq/`;

  return (
    <PlatformStateProviders
      wsAddress={drfqWsAddress}
      StoreProvider={DrfqPlatformStateStoreProvider}
      PlatformStateViewProvider={DrfqPlatformStateViewProvider}
      fetchSnapshot={getDrfqPlatformState}
      usePlatformState={useDrfqPlatformState}
      usePlatformStateActions={useDrfqPlatformStateActions}
    >
      {children}
    </PlatformStateProviders>
  );
}

function PlatformStateProviders({
  children,
  wsAddress,
  StoreProvider,
  PlatformStateViewProvider,
  fetchSnapshot,
  usePlatformState,
  usePlatformStateActions,
  useNanoSeconds = false,
}: PlatformStateProvidersProps) {
  return (
    <PlatformStateWebsocketProvider
      useNanoSeconds={useNanoSeconds}
      wsAddress={wsAddress}
    >
      <PlatformStateSubscriptionProvider>
        <StoreProvider>
          <PlatformStateFetcherProvider
            fetchSnapshot={fetchSnapshot}
            usePlatformStateActions={usePlatformStateActions}
          >
            <PlatformStateViewProvider usePlatformState={usePlatformState}>
              {children}
            </PlatformStateViewProvider>
          </PlatformStateFetcherProvider>
        </StoreProvider>
      </PlatformStateSubscriptionProvider>
    </PlatformStateWebsocketProvider>
  );
}

export const useUMPlatformCreationDisabled = () => {
  const {
    data: { platformState },
  } = useUMPlatformStateView();
  return platformState !== parsedPlatformStateValues.AVAILABLE;
};

export const useUMPlatformKillDisabled = () => {
  const {
    data: { platformState },
  } = useUMPlatformStateView();
  return platformState === parsedPlatformStateValues.UNAVAILABLE;
};

export const useFsPlatformCreationDisabled = () => {
  const {
    data: { platformState },
  } = useFsPlatformStateView();
  return platformState !== parsedPlatformStateValues.AVAILABLE;
};

export const useFsPlatformKillDisabled = () => {
  const {
    data: { platformState },
  } = useFsPlatformStateView();
  return platformState === parsedPlatformStateValues.UNAVAILABLE;
};
